import {
  PolicyTransactionSearchResponse,
  PolicyTransactionPostRequest,
  PolicyTransactionType,
  PolicyGetResponse,
} from '@lio/api-types';
import { ArrayItem } from '@utils/commonTypes';

export type PolicyTransaction = ArrayItem<PolicyTransactionSearchResponse['items']>;
export type PolicyRelatedSources = ArrayItem<PolicyGetResponse['relatedPolicySources']>;
export type PolicyRelatedDestinations = ArrayItem<PolicyGetResponse['relatedPolicyDestinations']>;

export type EndorsementTransaction = PolicyTransactionPostRequest['policyEndorsementTransaction'];
export type EndorsementReason = NonNullable<EndorsementTransaction>['reason'];

export type CancelationRequest = {
  reason: string;
  source: string;
  method: string;
  effectiveDate: Date | null;
  notes: string;
  confirmation: boolean;
  losses: string;
};

export const PolicyTransactionTypes = {
  NewBusiness: 'NewBusiness' as PolicyTransactionType,
  Endorsement: 'Endorsement' as PolicyTransactionType,
  Renewal: 'Renewal' as PolicyTransactionType,
  ConditionalRenewal: 'ConditionalRenewal' as PolicyTransactionType,
  Reinstatement: 'Reinstatement' as PolicyTransactionType,
  Rewrite: 'Rewrite' as PolicyTransactionType,
  Cancelation: 'Cancelation' as PolicyTransactionType,
  BillingPlanEndorsement: 'BillingPlanEndorsement' as PolicyTransactionType,
  DoNotRenew: 'DoNotRenew' as PolicyTransactionType,
  RescindDoNotRenew: 'RescindDoNotRenew' as PolicyTransactionType,
  DoNotReinstate: 'DoNotReinstate' as PolicyTransactionType,
  RescindDoNotReinstate: 'RescindDoNotReinstate' as PolicyTransactionType,
  ManageRoles: 'BrokerReassignment' as PolicyTransactionType,
};

export const PolicyTransactionTypesToLabel = {
  [PolicyTransactionTypes.NewBusiness]: 'New Business',
  [PolicyTransactionTypes.Endorsement]: 'Endorsement',
  [PolicyTransactionTypes.Renewal]: 'Renewal',
  [PolicyTransactionTypes.ConditionalRenewal]: 'Conditional Renewal',
  [PolicyTransactionTypes.Reinstatement]: 'Reinstatement',
  [PolicyTransactionTypes.Rewrite]: 'Rewrite',
  [PolicyTransactionTypes.Cancelation]: 'Cancelation',
  [PolicyTransactionTypes.BillingPlanEndorsement]: 'Billing Plan Endorsement',
  [PolicyTransactionTypes.DoNotRenew]: 'Do Not Renew',
  [PolicyTransactionTypes.RescindDoNotRenew]: 'Rescind Do Not Renew',
  [PolicyTransactionTypes.DoNotReinstate]: 'Do Not Reinstate',
  [PolicyTransactionTypes.RescindDoNotReinstate]: 'Rescind Do Not Reinstate',
  [PolicyTransactionTypes.ManageRoles]: 'Manage Roles',
};

export enum PolicyTransactionStatus {
  Created = 'Created',
  Canceled = 'Canceled',
  Processed = 'Processed',
  RolledBack = 'RolledBack',
}

export enum PolicyCancelationSource {
  Company = 'Company',
  Insured = 'Insured',
}

export enum PolicyCancelationReason {
  CompanyRequest = 'CompanyRequest',
  InsuredRequest = 'InsuredRequest',
  UnderwritingDecision = 'UnderwritingDecision',
  NonPayment = 'NonPayment',
  NonRenewal = 'NonRenewal',
  RewrittenToOtherCarrier = 'RewrittenToOtherCarrier',
  NotTaken = 'NotTaken',
  Other = 'Other',
}

export enum PolicyEndorsementReason {
  AdditionalInsuredAdded = 'AdditionalInsuredAdded',
  AdditionalInsuredChange = 'AdditionalInsuredChanged',
  AdditionalInsuredRemoved = 'AdditionalInsuredRemoved',
  AdditionalNamedInsuredAdded = 'AdditionalNamedInsuredAdded',
  AdditionalNamedInsuredChanged = 'AdditionalNamedInsuredChanged',
  AdditionalNamedInsuredRemoved = 'AdditionalNamedInsuredRemoved',
  AdditionalInterestAdded = 'AdditionalInterestAdded',
  AdditionalInterestChanged = 'AdditionalInterestChanged',
  AdditionalInterestRemoved = 'AdditionalInterestRemoved',
  BillAddressChange = 'BillAddressChange',
  BuildingAdded = 'BuildingAdded',
  BuildingChanged = 'BuildingChanged',
  BuildingRemoved = 'BuildingRemoved',
  ClassificationAdded = 'ClassificationAdded',
  ClassificationChanged = 'ClassificationChanged',
  ClassificationRemoved = 'ClassificationRemoved',
  CoverageAdded = 'CoverageAdded',
  CoverageChanged = 'CoverageChanged',
  CoverageRemoved = 'CoverageRemoved',
  LimitChanged = 'LimitChanged',
  LocationAdded = 'LocationAdded',
  LocationChanged = 'LocationChanged',
  LocationRemoved = 'LocationRemoved',
  MailingAddressChange = 'MailingAddressChange',
  NamedInsuredChange = 'NamedInsuredChange',
  PolicyCorrection = 'PolicyCorrection',
  PropertyManagerAdded = 'PropertyManagerAdded',
  PropertyManagerChanged = 'PropertyManagerChanged',
  PropertyManagerRemoved = 'PropertyManagerRemoved',
  RatingModifierAdded = 'RatingModifierAdded',
  RatingModifierChanged = 'RatingModifierChanged',
  RatingModifierRemoved = 'RatingModifierRemoved',
  SprinklerCreditAdded = 'SprinklerCreditAdded',
  SprinklerCreditRemoved = 'SprinklerCreditRemoved',
  Other = 'Other',
  VehicleAdded = 'VehicleAdded',
  VehicleChanged = 'VehicleChanged',
  VehicleDeleted = 'VehicleDeleted',
}

export const endorsementReasons = [
  {
    value: PolicyEndorsementReason.AdditionalInsuredAdded as EndorsementReason,
    label: 'Additional Insured Added',
  },
  {
    value: PolicyEndorsementReason.AdditionalInsuredChange as EndorsementReason,
    label: 'Additional Insured Changed',
  },
  {
    value: PolicyEndorsementReason.AdditionalInsuredRemoved as EndorsementReason,
    label: 'Additional Insured Removed',
  },
  {
    value: PolicyEndorsementReason.AdditionalNamedInsuredAdded as EndorsementReason,
    label: 'Additional Named Insured Added',
  },
  {
    value: PolicyEndorsementReason.AdditionalNamedInsuredChanged as EndorsementReason,
    label: 'Additional Named Insured Changed',
  },
  {
    value: PolicyEndorsementReason.AdditionalNamedInsuredRemoved as EndorsementReason,
    label: 'Additional Named Insured Removed',
  },
  {
    value: PolicyEndorsementReason.AdditionalInterestAdded as EndorsementReason,
    label: 'Additional Interest Added',
  },
  {
    value: PolicyEndorsementReason.AdditionalInterestChanged as EndorsementReason,
    label: 'Additional Interest Changed',
  },
  {
    value: PolicyEndorsementReason.AdditionalInterestRemoved as EndorsementReason,
    label: 'Additional Interest Removed',
  },
  {
    value: PolicyEndorsementReason.BillAddressChange as EndorsementReason,
    label: 'Bill Address Change',
  },
  {
    value: PolicyEndorsementReason.BuildingAdded as EndorsementReason,
    label: 'Building Added',
  },
  {
    value: PolicyEndorsementReason.BuildingChanged as EndorsementReason,
    label: 'Building Changed',
  },
  {
    value: PolicyEndorsementReason.BuildingRemoved as EndorsementReason,
    label: 'Building Removed',
  },
  {
    value: PolicyEndorsementReason.ClassificationAdded as EndorsementReason,
    label: 'Classification Added',
  },
  {
    value: PolicyEndorsementReason.ClassificationChanged as EndorsementReason,
    label: 'Classification Changed',
  },
  {
    value: PolicyEndorsementReason.ClassificationRemoved as EndorsementReason,
    label: 'Classification Removed',
  },
  {
    value: PolicyEndorsementReason.CoverageAdded as EndorsementReason,
    label: 'Coverage Added',
  },
  {
    value: PolicyEndorsementReason.CoverageChanged as EndorsementReason,
    label: 'Coverage Changed',
  },
  {
    value: PolicyEndorsementReason.CoverageRemoved as EndorsementReason,
    label: 'Coverage Removed',
  },
  {
    value: PolicyEndorsementReason.LimitChanged as EndorsementReason,
    label: 'Limit Changed',
  },
  {
    value: PolicyEndorsementReason.LocationAdded as EndorsementReason,
    label: 'Location Added',
  },
  {
    value: PolicyEndorsementReason.LocationChanged as EndorsementReason,
    label: 'Location Changed',
  },
  {
    value: PolicyEndorsementReason.LocationRemoved as EndorsementReason,
    label: 'Location Removed',
  },
  {
    value: PolicyEndorsementReason.MailingAddressChange as EndorsementReason,
    label: 'Mailing Address Change',
  },
  {
    value: PolicyEndorsementReason.NamedInsuredChange as EndorsementReason,
    label: 'Named Insured Change',
  },
  {
    value: PolicyEndorsementReason.PolicyCorrection as EndorsementReason,
    label: 'Policy Correction',
  },
  {
    value: PolicyEndorsementReason.PropertyManagerAdded as EndorsementReason,
    label: 'Property Manager Added',
  },
  {
    value: PolicyEndorsementReason.PropertyManagerChanged as EndorsementReason,
    label: 'Property Manager Changed',
  },
  {
    value: PolicyEndorsementReason.PropertyManagerRemoved as EndorsementReason,
    label: 'Property Manager Removed',
  },
  {
    value: PolicyEndorsementReason.RatingModifierAdded as EndorsementReason,
    label: 'Rating Modifier Added',
  },
  {
    value: PolicyEndorsementReason.RatingModifierChanged as EndorsementReason,
    label: 'Rating Modifier Changed',
  },
  {
    value: PolicyEndorsementReason.RatingModifierRemoved as EndorsementReason,
    label: 'Rating Modifier Removed',
  },
  {
    value: PolicyEndorsementReason.SprinklerCreditAdded as EndorsementReason,
    label: 'Sprinkler Credit Added',
  },
  {
    value: PolicyEndorsementReason.SprinklerCreditRemoved as EndorsementReason,
    label: 'Sprinkler Credit Removed',
  },
  { value: PolicyEndorsementReason.Other as EndorsementReason, label: 'Other' },
  {
    value: PolicyEndorsementReason.VehicleAdded as EndorsementReason,
    label: 'Vehicle Added',
  },
  {
    value: PolicyEndorsementReason.VehicleChanged as EndorsementReason,
    label: 'Vehicle Changed',
  },
  {
    value: PolicyEndorsementReason.VehicleDeleted as EndorsementReason,
    label: 'Vehicle Deleted',
  },
];
export enum PolicyCancelationMethod {
  ProRata = 'ProRata',
  ShortRate = 'ShortRate',
  Flat = 'Flat',
}

export enum PolicyReinstatementReason {
  UnderwritingDecision = 'UnderwritingDecision',
  InsuredRequest = 'InsuredRequest',
  PremiumPaid = 'PremiumPaid',
  Other = 'Other',
}

export enum DialogType {
  Confirmation = 'Confirmation',
}

export enum PolicyDialogType {
  Cancelation = 'Cancelation',
  Reinstatement = 'Reinstatement',
  Rescindment = 'Rescindment',
  Rewrite = 'Rewrite',
  Renew = 'Renewal',
  CreateLinkedPolicy = 'CreateLinkedPolicy',
  ManageRoles = 'ManageRoles',
  BrokerReassignment = 'BrokerReassignment',
  Messages = 'Messages',
  Notes = 'Notes',
  MarkForReviewAtRenewal = 'MarkForReviewAtRenewal',
  RemoveReviewAtRenewal = 'RemoveReviewAtRenewal',
  ReviewAtRenewalActivity = 'ReviewAtRenewalActivity',
  ExternalEmails = 'ExternalEmails',
  ViewBORTransfer = 'ViewBORTransfer',
  DocumentArchive = 'DocumentArchive',
}

export enum RenewDialogType {
  DoNotRenew = 'DoNotRenew',
  DoNotRenewView = 'DoNotRenewView',
  RescindDoNotRenew = 'RescindDoNotRenew',
  ConditionalRenewal = 'ConditionalRenewal',
  ConditionalRenewalView = 'ConditionalRenewalView',
}

export enum EndorsementPolicyDialogType {
  Endorsement = 'Endorsement',
  SequenceEndorsement = 'SequenceEndorsement',
  UpdateBillPlan = 'UpdateBillPlan',
}

export type AllPolicyDialogTypes = EndorsementPolicyDialogType | PolicyDialogType | RenewDialogType | DialogType;

export const TransactionType: Record<string, string> = {
  NewBusiness: 'New Business',
  Endorsement: 'Endorsement',
  Renewal: 'Renewal',
  ConditionalRenewal: 'Conditional Renewal',
  Reinstatement: 'Reinstatement',
  Rewrite: 'Rewrite',
  Cancelation: 'Cancellation',
  BillingPlanEndorsement: 'Billing Plan Endorsement',
  DoNotRenew: 'Marked for Nonrenewal',
  RescindDoNotRenew: 'Rescinded Nonrenewal',
  DoNotReinstate: 'Do Not Reinstate',
  RescindDoNotReinstate: 'Rescind Do Not Reinstate',
  BrokerReassignment: 'Producer Reassigned',
};

export const PolicyTransactionIdx = {
  [PolicyDialogType.Reinstatement]: 'policyReinstatementTransaction',
  [PolicyDialogType.Cancelation]: 'policyCancelationTransaction',
};
